import { z } from "zod";
import { ENGAGEMENT_SHAPES, SIZE_TIERS } from "../intake/classification";
import { FINDING_SEVERITIES, FINDING_TYPES } from "../intake/findings";
import { documentViewSchema } from "./documents";

/** Purchase requests and the conversation (WP-2.1). Shared by the API and the web app. */

export const REQUEST_STATUSES = [
  "DRAFT",
  "SUBMITTED",
  "IN_REVIEW",
  "RETURNED_TO_REQUESTER",
  "SOURCING",
  "APPROVED",
  "REJECTED",
  /** A draft closed after reminders without activity (WP-3.5, ADR-0024); the requester can reopen it. */
  "ABANDONED",
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/** Statuses in which a request belongs to its requester alone (ADR-0022, ADR-0024). */
export const PRIVATE_STATUSES: ReadonlySet<RequestStatus> = new Set(["DRAFT", "ABANDONED"]);
/** Statuses in which the requester can still change the request. */
export const OPEN_STATUSES: ReadonlySet<RequestStatus> = new Set(["DRAFT", "RETURNED_TO_REQUESTER"]);

export const FIELD_VALUE_STATUSES = ["CAPTURED", "NOT_APPLICABLE", "NOT_PROVIDED"] as const;
export type FieldValueStatus = (typeof FIELD_VALUE_STATUSES)[number];
export const VALUE_SOURCES = ["REQUESTER", "DOCUMENT", "DIRECTORY", "SYSTEM"] as const;
export type ValueSource = (typeof VALUE_SOURCES)[number];

/** "PR-000042" — the number requesters and buyers quote. */
export function requestReference(number: number): string {
  return `PR-${String(number).padStart(6, "0")}`;
}

export const createRequestSchema = z.object({ language: z.enum(["it", "en"]).optional() });
export type CreateRequest = z.infer<typeof createRequestSchema>;

/** Longest message a requester can send in one turn (longer material belongs in a document). */
export const MAX_TURN_MESSAGE_LENGTH = 4000;

// C0 control characters except tab, line feed and carriage return (NUL breaks Postgres text).
// biome-ignore lint/suspicious/noControlCharactersInRegex: stripping control characters is the point
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

/**
 * Requesters can type or paste anything: the message is normalised (Unicode NFC, control
 * characters removed, runs of blank lines collapsed) before it is validated and stored.
 */
export function cleanMessage(text: string): string {
  return text
    .normalize("NFC")
    .replace(CONTROL_CHARACTERS, "")
    .replace(/\r\n?/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export const turnRequestSchema = z.object({
  message: z.string().transform(cleanMessage).pipe(z.string().min(1).max(MAX_TURN_MESSAGE_LENGTH)),
});
export type TurnRequest = z.infer<typeof turnRequestSchema>;

/** PUT /api/requests/:id/classification — the requester sets the classification (WP-2.6). */
export const classificationRequestSchema = z.object({
  engagementShape: z.enum(ENGAGEMENT_SHAPES),
  sizeTier: z.enum(SIZE_TIERS),
});
export type ClassificationRequest = z.infer<typeof classificationRequestSchema>;

/** POST /api/requests/:id/submit (WP-2.4, ADR-0014). */
export const submitRequestSchema = z.object({
  /** Record the open skippable elements as not provided and submit. */
  anyway: z.boolean().default(false),
});
export type SubmitRequest = z.infer<typeof submitRequestSchema>;

/** Longest value a requester can type into one element of the live panel. */
export const MAX_FIELD_VALUE_LENGTH = 4000;

const editedText = z.string().transform(cleanMessage).pipe(z.string().min(1).max(MAX_FIELD_VALUE_LENGTH));

/** PATCH /api/requests/:id/fields/:key — an inline edit in the live panel (ADR-0014). */
export const fieldEditParamsSchema = z.object({
  id: z.uuid(),
  key: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
});
export const fieldEditRequestSchema = z.object({
  /** Text (also money, duration, choice value), a list of items, or yes/no. */
  value: z.union([editedText, z.array(editedText).min(1).max(50), z.boolean()]),
});
export type FieldEditRequest = z.infer<typeof fieldEditRequestSchema>;

/** Why an edit was refused (400 `details.reason`; the web app shows a localised message). */
export const FIELD_EDIT_REJECTIONS = ["unknown_field", "read_only", "invalid_value", "budget_not_a_figure"] as const;
export type FieldEditRejection = (typeof FIELD_EDIT_REJECTIONS)[number];

export const requestSummarySchema = z.object({
  id: z.uuid(),
  reference: z.string(),
  title: z.string().nullable(),
  status: z.enum(REQUEST_STATUSES),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RequestSummary = z.infer<typeof requestSummarySchema>;

/** A stored field value: text, list, yes/no (boolean) or none. */
export const fieldValueSchema = z.union([z.string(), z.array(z.string()), z.boolean(), z.null()]);
export type FieldValue = z.infer<typeof fieldValueSchema>;

export const requestFieldViewSchema = z.object({
  key: z.string(),
  value: fieldValueSchema,
  status: z.enum(FIELD_VALUE_STATUSES),
  source: z.enum(VALUE_SOURCES),
  evidence: z.string().nullable(),
  updatedAt: z.string(),
});
export type RequestFieldView = z.infer<typeof requestFieldViewSchema>;

export const findingViewSchema = z.object({
  id: z.uuid(),
  type: z.enum(FINDING_TYPES),
  severity: z.enum(FINDING_SEVERITIES),
  message: z.string(),
  evidence: z.string().nullable(),
  /** MODEL = raised in a conversation turn, SYSTEM = by code, REVIEW = by the background review. */
  origin: z.enum(["MODEL", "SYSTEM", "REVIEW"]),
  createdAt: z.string(),
  /** Set when the requester's answer closed the point (WP-2.7); `resolution` says how. */
  resolvedAt: z.string().nullable(),
  resolution: z.string().nullable(),
});
export type FindingView = z.infer<typeof findingViewSchema>;

export const gateViewSchema = z.object({
  canSubmit: z.boolean(),
  applicableMandatory: z.array(z.string()),
  openMandatory: z.array(z.string()),
  waived: z.array(z.string()),
  notProvided: z.array(z.string()),
  /** Open elements "submit anyway" would record as not provided (everything but budget / legal entity). */
  skippable: z.array(z.string()),
  /** The request is classified and every open element is skippable (WP-2.4). True whenever canSubmit is. */
  canSubmitAnyway: z.boolean(),
});
export type GateView = z.infer<typeof gateViewSchema>;

export const messageViewSchema = z.object({
  id: z.uuid(),
  role: z.enum(["USER", "ASSISTANT"]),
  content: z.string(),
  status: z.enum(["COMPLETE", "FAILED"]),
  createdAt: z.string(),
});
export type MessageView = z.infer<typeof messageViewSchema>;

/** The live panel (ADR-0014): everything except the messages. */
export const requestPanelSchema = z.object({
  id: z.uuid(),
  /** Only the requester converses and edits (the UI hides those controls from anyone else). */
  requesterId: z.uuid(),
  reference: z.string(),
  title: z.string().nullable(),
  status: z.enum(REQUEST_STATUSES),
  language: z.enum(["it", "en"]),
  engagementShape: z.enum(ENGAGEMENT_SHAPES).nullable(),
  sizeTier: z.enum(SIZE_TIERS).nullable(),
  /** REQUESTER: set in the panel (the model no longer changes it); MODEL: proposed by the conversation. */
  classificationSource: z.enum(["MODEL", "REQUESTER"]).nullable(),
  submittedAt: z.string().nullable(),
  fields: z.array(requestFieldViewSchema),
  findings: z.array(findingViewSchema),
  gate: gateViewSchema,
  /** Attached documents, oldest first (WP-2.2). */
  documents: z.array(documentViewSchema),
  /**
   * What procurement told the requester (WP-3.1): the buyer's questions while the request is
   * returned, the reason once it is rejected; otherwise null.
   */
  buyerNote: z.object({ kind: z.enum(["questions", "rejection"]), text: z.string(), createdAt: z.string() }).nullable(),
  /** When an abandoned draft was closed for inactivity (ADR-0024); otherwise null. */
  abandonedAt: z.string().nullable(),
});
export type RequestPanel = z.infer<typeof requestPanelSchema>;

/** instant = a social message answered by code (no model call); full = the conversation model. */
export const TURN_LANES = ["instant", "full"] as const;
export type TurnLane = (typeof TURN_LANES)[number];

export const requestDetailSchema = requestPanelSchema.extend({ messages: z.array(messageViewSchema) });
export type RequestDetail = z.infer<typeof requestDetailSchema>;

/**
 * Server-sent events of POST /api/requests/:id/turns, in order:
 * `turn.started` → `reply.delta`* → `case.updated` → `turn.done`, or `turn.error` (then the stream ends).
 * `turn.started` comes as soon as the message is stored (before any model call); `lane` says
 * whether the reply is an instant one (greetings, thanks — no model call) or a full turn.
 */
export type TurnEvent =
  | { event: "turn.started"; data: { userMessageId: string; lane: TurnLane } }
  | { event: "reply.delta"; data: { text: string } }
  | { event: "case.updated"; data: RequestPanel }
  | {
      event: "turn.done";
      /** suggestSubmit: the model thinks the request is ready — the UI highlights Review & submit (it never submits). */
      data: { userMessageId: string; assistantMessageId: string; suggestSubmit: boolean };
    }
  | { event: "turn.error"; data: { code: string; message: string; userMessageId: string | null } };
